import { useState, useEffect } from "react";
import {
  Item,
  getItemById,
  ITEM_TYPE_DOCUMENT,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LINK,
} from "@/pages/workspace/utils/workspaceItemUtils";
import useWorkspace from "@/hooks/useWorkspace";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import Workspace from "@/models/workspace/Workspace";

interface UseFilesTabItemDragAndDropProps {
  workspaceId: string;
  currentItems: Item[];
  workspace: Workspace | null;
  /** Whether the user may move items at all (owner or shared Write). */
  canMoveItems: boolean;
}
export type BusyAction =
  | "favorite"
  | "subscription"
  | "sync"
  | "delete"
  | "reindex"
  | "movefolder"
  | "movedocument"
  | "movelink";

const moveBusyAction = (type?: Item["type"]): BusyAction => {
  if (type === ITEM_TYPE_FOLDER) return "movefolder";
  if (type === ITEM_TYPE_LINK) return "movelink";
  return "movedocument";
};

/** Only documents and folders are name-checked in their target, so only they get that hint. */
const moveFailureMessage = (type?: Item["type"]): string =>
  type === ITEM_TYPE_LINK
    ? "Kunne ikke flytte linket. Prøv igen."
    : "Fejl under flytning af elementer. Tjek venligst at mappen ikke allerede har et element med samme navn.";

/** "root" for workspace root, or folder id for a path segment */
export type PathSegmentDropTarget = string | null;

interface UseFilesTabItemDragAndDropReturn {
  draggedItem: string | null;
  dropTarget: string | null;
  pathSegmentDropTarget: PathSegmentDropTarget;
  itemBusy: Record<string, boolean>;
  busyReason: Record<string, string>;
  setBusy: (itemId: string, action: BusyAction) => void;
  clearBusy: (itemId: string) => void;
  handleDragStart: (e: React.DragEvent, id: string) => void;
  handleDragOver: (e: React.DragEvent, id: string) => void;
  handleDragLeave: () => void;
  handleDrop: (
    e: React.DragEvent,
    targetId: string,
    draggedItem: string
  ) => Promise<void>;
  handlePathSegmentDragOver: (targetFolderId: string | "root") => void;
  handlePathSegmentDragLeave: () => void;
  handleDropOnPathSegment: (
    e: React.DragEvent,
    targetFolderId: string | null
  ) => Promise<void>;
}

/**
 * Hook for handling drag and drop functionality in FilesTab
 *
 * Manages:
 * - Drag and drop state (dragged item, drop target)
 * - Item busy states during move operations
 * - Move operations for both folders and documents
 *
 * Handles moving items (folders and documents) within the workspace by dragging
 * them to different folders. The hook manages visual feedback during drag operations
 * and tracks busy states while moves are in progress.
 */
export const useFilesTabItemDragAndDrop = ({
  workspaceId,
  currentItems,
  workspace,
  canMoveItems,
}: UseFilesTabItemDragAndDropProps): UseFilesTabItemDragAndDropReturn => {
  const { moveFolder, moveDocuments, moveLink } = useWorkspace();

  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [pathSegmentDropTarget, setPathSegmentDropTarget] =
    useState<PathSegmentDropTarget>(null);
  const [itemBusy, setItemBusy] = useState<Record<string, boolean>>({});
  const [busyReason, setBusyReason] = useState<Record<string, string>>({});

  const handleDragStart = (e: React.DragEvent, id: string) => {
    // A row's <a>/<img> children are natively draggable and their dragstart bubbles
    // here regardless of the row's own draggable flag, so refuse the drag itself.
    if (!canMoveItems) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("text/plain", id);
    setDraggedItem(id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    const item = currentItems.find((item) => item.id === id);
    if (item && item.type === ITEM_TYPE_FOLDER && id !== draggedItem) {
      setDropTarget(id);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const setBusy = (itemId: string, action: BusyAction) => {
    const getBusyMessage = (): string => {
      switch (action) {
        case "sync":
          return "Synkroniserer";
        case "delete":
          return "Sletter";
        case "reindex":
          return "Klargører dokument";
        case "favorite":
          return "Opdaterer favorit";
        case "subscription":
          return "Opdaterer abonnering";
        case "movefolder":
          return "Flytter mappe";
        case "movedocument":
          return "Flytter dokument";
        case "movelink":
          return "Flytter link";
        default:
          return "Opdaterer";
      }
    };

    setItemBusy((prev) => ({
      ...prev,
      [itemId]: true,
    }));
    setBusyReason((prev) => ({
      ...prev,
      [itemId]: getBusyMessage() + "...",
    }));
  };

  const clearBusy = (itemId: string) => {
    setItemBusy((prev) => ({
      ...prev,
      [itemId]: false,
    }));
    setBusyReason((prev) => ({
      ...prev,
      [itemId]: "",
    }));
  };

  const handleDrop = async (
    e: React.DragEvent,
    targetId: string,
    draggedItemId: string
  ) => {
    const sourceItem = currentItems.find((item) => item.id === draggedItemId);
    try {
      if (targetId === draggedItemId) {
        return;
      }
      e.preventDefault();
      const targetItem = currentItems.find((item) => item.id === targetId);

      // setItemBusy({
      //   [draggedItemId]: true,
      // });
      // const reason =
      //   sourceItem && sourceItem.type === "folder"
      //     ? "Flytter mappe"
      //     : "Flytter dokument";
      // setBusyReason({
      //   [draggedItemId]: reason,
      // });
      setBusy(draggedItemId, moveBusyAction(sourceItem?.type));

      if (
        targetItem &&
        targetItem.type === ITEM_TYPE_FOLDER &&
        sourceItem &&
        sourceItem.type === ITEM_TYPE_FOLDER
      ) {
        await moveFolder.mutateAsync({
          workspaceId,
          requestBody: {
            folderId: sourceItem.id,
            newFolderLocation: targetId,
          },
        });
      }
      if (
        targetItem &&
        targetItem.type === ITEM_TYPE_FOLDER &&
        sourceItem &&
        sourceItem.type === ITEM_TYPE_DOCUMENT
      ) {
        await moveDocuments.mutateAsync({
          workspaceId,
          requestBody: {
            newFolderId: targetId,
            documentIds: [sourceItem.id],
          },
        });
      }
      if (
        targetItem &&
        targetItem.type === ITEM_TYPE_FOLDER &&
        sourceItem &&
        sourceItem.type === ITEM_TYPE_LINK
      ) {
        await moveLink.mutateAsync({
          workspaceId,
          linkId: sourceItem.id,
          requestBody: { newFolderId: targetId },
        });
      }
    } catch (error) {
      logger.error("Failed to move workspace item by drag and drop", {
        operation: "moveWorkspaceItemByDrop",
        error,
        workspaceId,
        itemId: draggedItemId,
        targetFolderId: targetId,
      });
      showErrorToast(moveFailureMessage(sourceItem?.type));
    } finally {
      // setItemBusy({
      //   [draggedItemId]: false,
      // });
      // setBusyReason({
      //   [draggedItemId]: "",
      // });
      clearBusy(draggedItemId);
      setDraggedItem(null);
      setDropTarget(null);
    }
  };

  const handlePathSegmentDragOver = (targetFolderId: string | "root") => {
    setPathSegmentDropTarget(targetFolderId);
  };

  const handlePathSegmentDragLeave = () => {
    setPathSegmentDropTarget(null);
  };

  const handleDropOnPathSegment = async (
    e: React.DragEvent,
    targetFolderId: string | null
  ) => {
    const draggedItemId = e.dataTransfer.getData("text/plain");
    if (!draggedItemId) return;
    e.preventDefault();
    setPathSegmentDropTarget(null);

    const sourceItem =
      currentItems.find((i) => i.id === draggedItemId) ??
      (workspace ? getItemById(workspace, draggedItemId) : null);
    if (!sourceItem) return;
    if (sourceItem.id === targetFolderId) return; // dropping on same folder

    const newFolderId =
      targetFolderId === null || targetFolderId === "root"
        ? undefined
        : targetFolderId;

    try {
      setBusy(draggedItemId, moveBusyAction(sourceItem.type));

      if (sourceItem.type === ITEM_TYPE_FOLDER) {
        await moveFolder.mutateAsync({
          workspaceId,
          requestBody: {
            folderId: sourceItem.id,
            newFolderLocation: newFolderId,
          },
        });
      } else if (sourceItem.type === ITEM_TYPE_LINK) {
        await moveLink.mutateAsync({
          workspaceId,
          linkId: sourceItem.id,
          requestBody: { newFolderId },
        });
      } else {
        await moveDocuments.mutateAsync({
          workspaceId,
          requestBody: {
            newFolderId,
            documentIds: [sourceItem.id],
          },
        });
      }
    } catch (error) {
      logger.error("Failed to move workspace item onto a path segment", {
        operation: "moveWorkspaceItemToPathSegment",
        error,
        workspaceId,
        itemId: draggedItemId,
        targetFolderId: newFolderId,
      });
      showErrorToast(moveFailureMessage(sourceItem.type));
    } finally {
      clearBusy(draggedItemId);
      setDraggedItem(null);
    }
  };

  // Effect: Clean up drag state on window dragend
  useEffect(() => {
    const handleDragEnd = () => {
      setDraggedItem(null);
      setDropTarget(null);
      setPathSegmentDropTarget(null);
    };

    window.addEventListener("dragend", handleDragEnd);
    return () => {
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  return {
    draggedItem,
    dropTarget,
    pathSegmentDropTarget,
    itemBusy,
    busyReason,
    setBusy,
    clearBusy,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePathSegmentDragOver,
    handlePathSegmentDragLeave,
    handleDropOnPathSegment,
  };
};
